"use client";

import React, { useState, useMemo, useCallback } from "react";
import { CoinOptionItem } from "./CoinOptionItem";
import { CoinSearchInput } from "./CoinSearchInput";
import { CoinProps, CoinData } from "./types";
import { Popover } from "@/src/components/Popover";

export const Coin: React.FC<CoinProps> = ({
  value,
  data,
  onChange,
  label = "Coin",
  disabled = false,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [isFource, setIsFource] = useState(false);
  const [opoverOpen, setOpoverOpen] = useState(false);

  const selectedCoin = useMemo(() => {
    return data?.find((item) => item.assetId === value?.assetId) || undefined;
  }, [data, value]);

  const filteredData = useMemo(() => {
    if (!searchValue || !data) {
      return data;
    }

    return data.filter((item) =>
      item.coin.toLowerCase().includes(searchValue.toLowerCase()),
    );
  }, [data, searchValue]);

  const handleSelectCoin = useCallback(
    (coin: CoinData) => {
      if (onChange) {
        onChange({
          assetId: coin.assetId,
          assetType: coin.assetType,
          currency: coin?.currency || "USD",
        });
      }
      setOpoverOpen(false);
      setSearchValue("");
    },
    [onChange],
  );

  const handleSearchChange = useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  const popoverContent = useMemo(() => {
    if (!filteredData || filteredData.length === 0) {
      return null;
    }

    return (
      <div className="rounded-2 border border-border_white_transparent10 bg-background_solid_tertiary shadow-lg max-h-[200px] overflow-y-auto w-full flex-col gap-2">
        <div className="p-1">
          {filteredData.map((coin) => (
            <CoinOptionItem
              data-openreplay-label="Coin · Coin · CoinOptionItem"
              key={coin.assetId}
              data={coin}
              selected={
                coin.assetId === selectedCoin?.assetId &&
                !!selectedCoin?.assetId
              }
              onClick={() => handleSelectCoin(coin)}
            />
          ))}
        </div>
      </div>
    );
  }, [filteredData, selectedCoin, handleSelectCoin]);

  return (
    <div className="flex flex-col gap-2">
      <label
        className="body_medium_s text-content_secondary"
        htmlFor="transaction-coin"
      >
        {label}
      </label>

      <Popover
        content={popoverContent}
        disabled={!data || data.length === 0 || disabled}
        trigger="click"
        placement="bottom-start"
        zIndex={9999}
        portalId="coin-portal-container"
        floatingStyles={{ width: "100%" }}
        onVisibilityChange={(e) => {
          if (!e && isFource) {
            return;
          }
          setOpoverOpen(e);
        }}
        open={opoverOpen}
      >
        <CoinSearchInput
          value={searchValue}
          onChange={handleSearchChange}
          selectedCoin={selectedCoin}
          placeholder="Search"
          isFource={isFource}
          disabled={disabled}
          onFocus={() => {
            setIsFource(true);
            setOpoverOpen(true);
          }}
          onBlur={() => {
            setTimeout(() => {
              setIsFource(false);
              setOpoverOpen(false);
            }, 200);
          }}
        />
      </Popover>
    </div>
  );
};
